/**
 * TetherCam のデバイス 1 台の設定（/shoot/devices/:name。Issue #790）。名乗った名前ごとに本番へ保存する（SetTetherCamDevice）。
 * 何を決められるかはデバイスが名乗る機能（v4 の features。tethercam/docs/tethercam-api.md §8）で決める:
 * - 座標（2 席ぶん: フィールド・手札・認識領域 1・2 × P1・P2）: そのデバイスの配信の生の絵の上で（features.stream。絵が補正サーバーに届いているとき）
 *   「自動で合わせる → P1 / P2 の枠」: 補正サーバーがこのデバイスの生の絵からマットを探し（POST /live/cams/autofit {device}）、
 *   選んだ席のフィールド・手札・認識領域 1・2 に入れる。保存は押してから（Issue #802）。マットは本番の Opus の当たりを補正サーバーが
 *   同梱のマットの枠との重ね合わせで詰める（Issue #817）。OBS の絵のように 2 枚写っていれば、マットごとに P1 / P2 のどちらに使うか選ぶ
 *   （1 回の検出で両方の席に入れられる）
 * - ゾーンの確認: フィールドの 4 点から、ゾーンの枠を生の絵へ戻して重ねる（utils/zoneProjection.js）。切り抜き（features.crops）の数も
 * - 音声認識の経路: 有効・音源（内蔵マイク / 外部ストリーミングの受信 / ストリーミング待機。対応しているものだけ）・受信の URL（features.speech）。
 *   保存すると補正サーバーが数秒でそのデバイスへ speech_source / speech_start / speech_stop を出す（デバイスの画面で変えたらそちらが勝つ）
 * 収録でどのデバイスをどの席・枠に使うかはここでは決めない。
 */
import { liveUrl } from '../components/liveBase';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/api';
import { usePageTitle } from '../appInfo';
import ShootNav from '../components/ShootNav';
import QuadSetupEditor from '../components/QuadSetupEditor';
import { useStill } from '../components/liveCorrect';
import { SLOTS, autofitSizes, fromDoc, slotsFromAutofit, toEntries } from '../utils/fieldPicker';
import { projectZones } from '../utils/zoneProjection';
import { DeviceCard, SPEECH_SOURCES, deviceName, deviceTitle, useLiveDevices, useSavedDevices } from './TetherCamDevices';

const SHOW_W = 640;
const ZONE_COLORS = { 1: '#4da3ff', 2: '#ff5a5a' };

/** 機能が使えるか（無い・supported でなければ false） */
export const supports = (features, name) => !!features?.[name]?.supported;

/** 選べる音源（対応しているものだけ）。features が無い（v3 以前）なら内蔵マイクだけ */
export function speechChoices(features) {
  if (!features) return ['mic'];
  return ['mic', 'pull', 'listen'].filter((s) => supports(features, `speech_${s}`));
}

/** 編集の状態（画素）→ デバイスの設定の entries（0〜1。配信の名前は持たない） */
export function deviceEntries(size, slots) {
  return toEntries(size, slots);
}

/** 自動で合わせたマットの合わせ方の言い方（補正サーバーの mats[].method。Issue #817） */
export const AUTOFIT_METHODS = {
  'claude+ecc': 'Opus の当たり → 重ね合わせ',
  zones: '印刷のゾーン',
  claude: 'Opus の当たりだけ（粗いので手で直してください）',
};

/** 複数のマットのうち m がどこにあるか（左 / 右・上 / 下。補正サーバーは左から並べて返す） */
export function matPlace(m, mats) {
  const centre = (x) => x.corners.reduce(([a, b], [u, v]) => [a + u / 4, b + v / 4], [0, 0]);
  const [x, y] = centre(m);
  const others = mats.filter((o) => o !== m).map(centre);
  if (!others.length) return '';
  const horizontal = others.every(([ox, oy]) => Math.abs(ox - x) >= Math.abs(oy - y));
  if (horizontal) return others.every(([ox]) => x < ox) ? '左' : others.every(([ox]) => x > ox) ? '右' : '真ん中';
  return others.every(([, oy]) => y < oy) ? '上' : others.every(([, oy]) => y > oy) ? '下' : '真ん中';
}

/** 自動で合わせる（席を選ぶ）。結果は replace で編集へ（保存は人が押してから）。マットが 2 枚あればマットごとに P1 / P2 を選ぶ */
export function useAutofit(name, size, fetchImpl) {
  const [replace, setReplace] = useState(null);
  const [busy, setBusy] = useState(null);       // 合わせている席
  const [mats, setMats] = useState(null);       // 2 枚以上あったときのマット（選ぶまで枠は変えない。次に合わせ直すまで残す）
  const apply = useCallback((seat, mat, slots) => {
    const how = AUTOFIT_METHODS[mat?.method] ? `（${AUTOFIT_METHODS[mat.method]}）` : '';
    setReplace({ seq: Date.now(), slots: slotsFromAutofit(mat.profiles, seat, size[0], size[1], slots),
      message: `P${seat} の枠を自動で合わせました${how}（保存すると使い始めます）` });
  }, [size]);
  const run = useCallback(async (seat, slots) => {
    const f = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
    if (!f || !size) return;
    setBusy(seat);
    setMats(null);
    const note = (message) => setReplace({ seq: Date.now(), slots: {}, message });
    try {
      const r = await f(liveUrl('/live/cams/autofit'), { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device: name, sizes: autofitSizes(slots, seat) }) });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) note(body?.error ?? `自動で合わせられません（${r.status}）`);
      else if (!body.found) note('プレイマットが見つかりませんでした（手で合わせてください）');
      else if ((body.mats ?? []).length > 1) {
        setMats(body.mats);
        note(`プレイマットが ${body.mats.length} 枚あります。マットごとに P1 / P2 のどちらに使うか選んでください`);
      } else apply(seat, body.mats?.[0] ?? { profiles: body.profiles }, slots);
    } catch (e) {
      note(`補正サーバーに届きません: ${e?.message ?? e}`);
    } finally {
      setBusy(null);
    }
  }, [name, size, fetchImpl, apply]);
  const actions = (slots) => [
    ...[1, 2].map((seat) => (
      <button key={seat} type="button" data-testid={`autofit-${seat}`} disabled={busy != null} onClick={() => run(seat, slots)}>
        {busy === seat ? '合わせています…' : `自動で合わせる → P${seat} の枠`}
      </button>
    )),
    ...(mats ? mats.flatMap((m) => [1, 2].map((seat) => (
      <button key={`mat-${m.index}-${seat}`} type="button" data-testid={`autofit-mat-${m.index}-${seat}`}
        onClick={() => apply(seat, m, slots)}>
        {`マット ${m.index}（${matPlace(m, mats)}）→ P${seat}`}
      </button>
    ))) : []),
  ];
  return { replace, actions };
}

function SpeechForm({ features, state, saved, onSave }) {
  const choices = speechChoices(features);
  const [enabled, setEnabled] = useState(saved?.enabled ?? state?.enabled ?? false);
  const [source, setSource] = useState(saved?.source ?? state?.source ?? choices[0]);
  const [url, setUrl] = useState(saved?.pull_url ?? state?.pull?.url ?? '');
  const [msg, setMsg] = useState(null);
  const save = async () => {
    try {
      await onSave({ enabled, source, ...(source === 'pull' ? { pull_url: url.trim() } : {}) });
      setMsg('保存しました（補正サーバーが数秒でこのデバイスに伝えます）');
    } catch (e) {
      setMsg(`保存できませんでした: ${e?.message ?? e}`);
    }
  };
  return (
    <section className="card" data-testid="speech-route" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
      <h3 className="card-title" style={{ margin: 0 }}>音声認識の経路</h3>
      <label><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="speech-enabled" /> 音声認識を使う</label>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {choices.map((s) => (
          <label key={s}><input type="radio" name="speech-source" checked={source === s} onChange={() => setSource(s)} data-testid={`speech-source-${s}`} /> {SPEECH_SOURCES[s]}</label>
        ))}
      </div>
      {source === 'pull' ? (
        <label>受信の URL{' '}
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/live/audio/<名前> か tcp://host:port" style={{ width: '100%' }} data-testid="speech-pull-url" />
        </label>
      ) : null}
      {source === 'listen' && state?.listen?.url ? <div style={{ color: '#b8c0d4' }}>待ち受けの口: {state.listen.url}（ほかの機器がここへ音を流す）</div> : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={save} disabled={source === 'pull' && !url.trim()} data-testid="speech-save">保存</button>
        {msg ? <span data-testid="speech-message" style={{ color: '#8a93a8' }}>{msg}</span> : null}
      </div>
    </section>
  );
}

function ZoneCheck({ entries, input, live, crops }) {
  const [w, h] = input?.size ?? [0, 0];
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setTick(Date.now()), 2000);
    return () => clearInterval(t);
  }, [live]);
  const raw = useStill(`/live/raw.jpg?src=${input?.name}&w=${SHOW_W * 2}&t=${tick}`, live && !!input?.fresh);
  const zones = useMemo(() => (w ? projectZones(entries, w, h) : []), [entries, w, h]);
  return (
    <section className="card" data-testid="zone-check" style={{ display: 'grid', gap: 8 }}>
      <h3 className="card-title" style={{ margin: 0 }}>ゾーンの確認</h3>
      <div style={{ fontSize: 13, color: '#b8c0d4' }}>
        フィールドの 4 点から出したゾーンの枠（P1 青・P2 赤）。カードの置き場とずれていたら 4 点を直してください。
        {crops ? ` このデバイスはゾーンを等倍で切り抜いて送ります（四角形 ${crops.regions ?? 0}・送った ${crops.sent ?? 0}・失敗 ${crops.failed ?? 0}）。` : ''}
      </div>
      <div style={{ position: 'relative', width: '100%', maxWidth: SHOW_W, aspectRatio: `${w / h}`, background: '#000' }}>
        {raw ? <img src={raw} alt="デバイスの生の絵" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} /> : null}
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          data-testid="zone-overlay">
          {zones.map((z) => (
            <polygon key={z.key} points={z.points.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" stroke={ZONE_COLORS[z.player]}
              strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      </div>
    </section>
  );
}

export default function TetherCamDevice({ fetchImpl = null }) {
  const { name: rawName } = useParams();
  const name = decodeURIComponent(rawName ?? '');
  usePageTitle(`TetherCam ${name}`);
  const devices = useLiveDevices(fetchImpl);
  const [saved, reload] = useSavedDevices();
  const live = (devices ?? []).find((d) => deviceName(d) === name) ?? null;
  const doc = saved?.find((s) => s.name === name)?.doc ?? null;
  const features = live?.state?.features ?? null;
  const input = live?.input;
  const size = input?.fresh ? input.size : null;
  const watch = !fetchImpl;

  const write = async (patch) => {
    const next = { entries: doc?.entries ?? [], ...(doc?.speech ? { speech: doc.speech } : {}), ...patch };
    await api.project('SetTetherCamDevice', { name, doc: next });
    await reload();
  };
  const initialSlots = useMemo(() => (size ? fromDoc({ entries: doc?.entries ?? [] }, size[0], size[1], false) : null),
    // 保存し直したときは作り直さない（編集の途中を消さない）。デバイスの配信の大きさが変わったら作り直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [size?.[0], size?.[1], saved === undefined]);

  const canCoords = !features || supports(features, 'stream');
  const autofit = useAutofit(name, size, fetchImpl);
  return (
    <>
    <ShootNav />
    <div className="page" data-testid="tethercam-device">
      <div className="page-header">
        <h2>{live ? deviceTitle(live) : name}</h2>
        <Link to="/shoot/devices">一覧へ</Link>
      </div>
      {devices === null ? <div style={{ color: '#ff8a8a', fontSize: 13 }}>補正サーバーに届きません（起きていないか、止まっています）</div> : null}
      {devices && !live ? <div data-testid="device-absent" style={{ fontSize: 13, color: '#f0c36b' }}>このデバイスは補正サーバーに来ていません（保存した設定だけ見られます）</div> : null}
      {live ? <DeviceCard d={live} title="今の状態" /> : null}
      {saved !== undefined && (supports(features, 'speech') || (!live && doc?.speech)) ? (
        <SpeechForm key={JSON.stringify(doc?.speech ?? null)} features={features} state={live?.state?.speech} saved={doc?.speech}
          onSave={(speech) => write({ speech })} />
      ) : null}
      {live && !supports(features, 'speech') && features ? <div style={{ fontSize: 13, color: '#8a93a8' }}>このデバイスは音声認識に対応していません</div> : null}
      {size && doc?.entries?.some((e) => e.field_type === 'field') ? (
        <ZoneCheck entries={doc.entries} input={input} live={watch} crops={supports(features, 'crops') ? live?.state?.crops : null} />
      ) : null}
      {!canCoords ? <div style={{ fontSize: 13, color: '#8a93a8' }}>このデバイスは配信に対応していないので、座標は決められません</div> : null}
      {canCoords && !size ? (
        <div data-testid="coords-waiting" style={{ fontSize: 13, color: '#f0c36b' }}>
          このデバイスの配信が補正サーバーに届くと、その絵の上で座標（2 席ぶん）を決められます
        </div>
      ) : null}
    </div>
    {canCoords && size && initialSlots ? (
      <div data-testid="coords-editor" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - var(--nav-h, 52px))' }}>
        <QuadSetupEditor
          width={size[0]} height={size[1]} slotDefs={SLOTS} initialSlots={initialSlots} title={`${name} の座標（2 席ぶん）`}
          liveUrl={`/live/stream?raw=${input.name}`} stillUrl={`/live/raw.jpg?src=${input.name}`}
          onSave={(slots) => write({ entries: deviceEntries({ width: size[0], height: size[1] }, slots) })}
          replace={autofit.replace} renderActions={autofit.actions}
        />
      </div>
    ) : null}
    </>
  );
}
